import React, { createContext, useContext, useId, useRef } from 'react'
import { AuthContext } from '../../context/auth.context'
import { CopyValue } from '../sso-workspace/copy-value'
import { useCopy } from '../clipboard/clipboard'
import { isImpersonating } from '../audit/use-audit-capabilities'
import { requestAtOf, requestIDOf } from './request-id'
import '../sso-workspace/sso-workspace.scss'
import './error-reference.scss'

// The words of the SSO sign-in error page's reference (owner copy).
export const REFERENCE_LABEL = 'Reference for support'
export const REFERENCE_HINT = 'If you contact support, include this so they can find what happened.'

// The Request log's page for one request, with when it failed (?at= bounds
// the lookup to a day either side).
export const requestLogHref = (requestID, at) =>
  `/admin/request-log/${encodeURIComponent(requestID)}${at ? `?at=${encodeURIComponent(at)}` : ''}`

// A tree with no auth context (a test that mocks it away): no session.
const NoAuth = createContext(null)

// Who may open the request: a super admin in their own session (the
// Request log is theirs; an impersonated session is someone else's).
const isSuperAdmin = auth => {
  const state = auth && auth.state
  return !!state && !!(state.isSysAdmin || state.isSysRoot) && !isImpersonating(state)
}

// The one error reference (owner 2026-10-06): a failed request's id, shown
// with the SSO error page's words and CopyValue, wherever an API error is
// shown to a person. `error` is anything an API layer hands on (see
// request-id.js); `requestID` (and `at`) name it directly. Nothing renders
// without an id: a network failure never reached the server.
//
// A super admin also gets the request itself in the Request log, in a new
// tab so the error and the page it happened on stay put. rel="opener", not
// noopener: the session lives in sessionStorage, which a new tab only
// inherits from its opener (a noopener tab opened signed out). `actions` sit
// in the same row -- where a "Contact support" action goes when the support
// inbox exists; add it here, and every error gets it.
//
// compact: the same reference on ONE line -- "Reference <id> · Copy", plus
// "Request log" for a super admin -- for a place with no room for the block,
// such as the device keypad's fixed message row (owner report 2026-10-08).
// The id ellipsizes rather than wraps; Copy always copies all of it.
export const ErrorReference = ({ error, requestID, at, actions, className, compact }) => {
  const auth = useContext(AuthContext || NoAuth)
  const fieldID = `error-reference-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const id = requestID || requestIDOf(error)
  if (!id) return null

  const when = at || requestAtOf(error)
  const superAdmin = isSuperAdmin(auth)

  if (compact) return <CompactReference id={id} when={when} superAdmin={superAdmin} className={className} />

  return (
    <div className={['err-ref', className].filter(Boolean).join(' ')} data-testid="error-reference">
      <CopyValue id={fieldID} label={REFERENCE_LABEL} value={id} hint={REFERENCE_HINT} size="sm" />
      {(superAdmin || actions) &&
        <div className="err-ref-actions" data-testid="error-reference-actions">
          {superAdmin &&
            // eslint-disable-next-line react/jsx-no-target-blank -- our own page, which needs the opener's session (see above)
            <a className="btn btn-sm sw-btn" href={requestLogHref(id, when)} target="_blank" rel="opener">Open in the Request log</a>}
          {actions}
        </div>}
    </div>
  )
}

const CompactReference = ({ id, when, superAdmin, className }) => {
  const copy = useCopy()
  const value = useRef(null)
  return (
    <div className={['err-ref-line', className].filter(Boolean).join(' ')} data-testid="error-reference">
      <span className="err-ref-line__label">Reference</span>
      <span ref={value} className="err-ref-line__id" title={id}>{id}</span>
      <button type="button" className="btn btn-outline-secondary err-ref-line__btn" aria-label={`copy ${REFERENCE_LABEL}`} onClick={() => copy.copy(id, value)}>
        {copy.copied ? 'Copied' : copy.manual ? copy.hint : 'Copy'}
      </button>
      {superAdmin &&
        // eslint-disable-next-line react/jsx-no-target-blank -- our own page, which needs the opener's session (see above)
        <a className="btn btn-outline-secondary err-ref-line__btn" href={requestLogHref(id, when)} target="_blank" rel="opener">Request log</a>}
    </div>
  )
}
